import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Chip,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FitnessCenter as TrainerIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const TrainerManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trainers, setTrainers] = useState([]);
  const [lessonCategories, setLessonCategories] = useState([]);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", email: "", hourly_cost: "" });
  
  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ full_name: "", email: "", hourly_cost: "" });
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState(null);
  
  // Category rate dialog state
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateDialogTrainer, setRateDialogTrainer] = useState(null);
  const [newRateCategory, setNewRateCategory] = useState("");
  const [newRateAmount, setNewRateAmount] = useState("");
  
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchTrainers = async () => {
    setLoading(true);
    setError("");
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers`,
        { headers }
      );
      if (response.status === 200) {
        setTrainers(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch trainers", err);
      setError("Nie udało się załadować listy trenerów");
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonCategories = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/lesson-categories`,
        { headers }
      );
      if (response.status === 200) {
        setLessonCategories(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch lesson categories", err);
    }
  };

  useEffect(() => {
    fetchTrainers();
    fetchLessonCategories();
  }, []);

  // Create trainer handlers
  const handleCreateClick = () => {
    setCreateForm({ full_name: "", email: "", hourly_cost: "" });
    setCreateDialogOpen(true);
  };

  const handleCreateSave = async () => {
    if (!createForm.full_name || !createForm.email) {
      setSnackbar({ open: true, message: "Imię i email są wymagane", severity: "error" });
      return;
    }
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers`,
        {
          full_name: createForm.full_name,
          email: createForm.email,
          hourly_cost: createForm.hourly_cost ? parseInt(createForm.hourly_cost) : null
        },
        { headers }
      );
      
      if (response.status === 200) {
        setTrainers(prev => [...prev, response.data]);
        setSnackbar({ open: true, message: "Dodano nowego trenera", severity: "success" });
        setCreateDialogOpen(false);
      }
    } catch (err) {
      console.error("Failed to create trainer", err);
      const errorMsg = err.response?.data?.detail || "Nie udało się dodać trenera";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  // Edit trainer handlers
  const handleEditClick = (trainer) => {
    setEditingTrainer(trainer);
    setEditForm({
      full_name: trainer.full_name,
      email: trainer.email,
      hourly_cost: trainer.hourly_cost?.toString() || ""
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingTrainer) return;
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${editingTrainer.id}`,
        {
          full_name: editForm.full_name || null,
          email: editForm.email || null,
          hourly_cost: editForm.hourly_cost ? parseInt(editForm.hourly_cost) : null
        },
        { headers }
      );
      
      if (response.status === 200) {
        setTrainers(prev => 
          prev.map(t => t.id === editingTrainer.id ? response.data : t)
        );
        setSnackbar({ open: true, message: "Zaktualizowano dane trenera", severity: "success" });
        setEditDialogOpen(false);
        setEditingTrainer(null);
      }
    } catch (err) {
      console.error("Failed to update trainer", err);
      const errorMsg = err.response?.data?.detail || "Nie udało się zaktualizować danych";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  // Delete trainer handlers
  const handleDeleteClick = (trainer) => {
    setTrainerToDelete(trainer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!trainerToDelete) return;
    
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerToDelete.id}`,
        { headers }
      );
      
      setTrainers(prev => prev.filter(t => t.id !== trainerToDelete.id));
      setSnackbar({ open: true, message: "Usunięto trenera", severity: "success" });
      setDeleteDialogOpen(false);
      setTrainerToDelete(null);
    } catch (err) {
      console.error("Failed to delete trainer", err);
      const errorMsg = err.response?.data?.detail || "Nie udało się usunąć trenera";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
      setDeleteDialogOpen(false);
    }
  };

  // Category rate handlers
  const handleOpenRateDialog = (trainer) => {
    setRateDialogTrainer(trainer);
    setNewRateCategory("");
    setNewRateAmount("");
    setRateDialogOpen(true);
  };

  const handleAddCategoryRate = async () => {
    if (!rateDialogTrainer || !newRateCategory || !newRateAmount) {
      setSnackbar({ open: true, message: "Wybierz kategorię i podaj stawkę", severity: "error" });
      return;
    }
    
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${rateDialogTrainer.id}/category-rates`,
        {
          lesson_category_id: parseInt(newRateCategory),
          hourly_rate: parseInt(newRateAmount)
        },
        { headers }
      );
      
      if (response.status === 200) {
        // Refresh trainers to get updated data
        await fetchTrainers();
        setNewRateCategory("");
        setNewRateAmount("");
        setSnackbar({ open: true, message: "Dodano stawkę dla kategorii", severity: "success" });
      }
    } catch (err) {
      console.error("Failed to add category rate", err);
      const errorMsg = err.response?.data?.detail || "Nie udało się dodać stawki";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  const handleDeleteCategoryRate = async (trainerId, rateId) => {
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/costs/trainers/${trainerId}/category-rates/${rateId}`,
        { headers }
      );
      
      await fetchTrainers();
      setSnackbar({ open: true, message: "Usunięto stawkę kategorii", severity: "success" });
    } catch (err) {
      console.error("Failed to delete category rate", err);
      const errorMsg = err.response?.data?.detail || "Nie udało się usunąć stawki";
      setSnackbar({ open: true, message: errorMsg, severity: "error" });
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "-";
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  // Get available categories for a trainer (ones not already assigned)
  const getAvailableCategoriesForTrainer = (trainer) => {
    const assignedCategoryIds = trainer.category_rates?.map(r => r.lesson_category_id) || [];
    return lessonCategories.filter(c => !assignedCategoryIds.includes(c.id));
  };

  // Update rateDialogTrainer whenever trainers list updates
  useEffect(() => {
    if (rateDialogTrainer) {
      const updatedTrainer = trainers.find(t => t.id === rateDialogTrainer.id);
      if (updatedTrainer) {
        setRateDialogTrainer(updatedTrainer);
      }
    }
  }, [trainers, rateDialogTrainer?.id]);

  return (
    <Box sx={{ width: "100%", p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrainerIcon color="primary" />
            Zarządzanie Trenerami
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dodawanie, edycja trenerów i ustawianie stawek godzinowych
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            Dodaj Trenera
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTrainers}
            disabled={loading}
          >
            Odśwież
          </Button>
        </Box>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Liczba trenerów
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {trainers.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Ze stawką domyślną
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {trainers.filter(t => t.hourly_cost > 0).length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                Ze stawkami kategorii
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {trainers.filter(t => t.category_rates?.length > 0).length}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Box>
          {trainers.map((trainer) => (
            <Accordion key={trainer.id} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <TrainerIcon color="primary" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {trainer.full_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trainer.email}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" color="text.secondary">
                      Stawka domyślna
                    </Typography>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600,
                        color: trainer.hourly_cost > 0 ? 'success.main' : 'text.secondary'
                      }}
                    >
                      {formatCurrency(trainer.hourly_cost)}
                    </Typography>
                  </Box>
                  {trainer.category_rates?.length > 0 && (
                    <Chip 
                      icon={<CategoryIcon />}
                      label={`${trainer.category_rates.length} kat.`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />
                
                {/* Category Rates Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon fontSize="small" />
                    Stawki według kategorii zajęć
                  </Typography>
                  
                  {trainer.category_rates?.length > 0 ? (
                    <List dense disablePadding>
                      {trainer.category_rates.map((rate) => (
                        <ListItem 
                          key={rate.id}
                          sx={{ 
                            bgcolor: 'grey.50', 
                            borderRadius: 1, 
                            mb: 0.5,
                            pl: 2
                          }}
                        >
                          <ListItemText
                            primary={rate.lesson_category_name}
                            secondary={formatCurrency(rate.hourly_rate)}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCategoryRate(trainer.id, rate.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, py: 1 }}>
                      Brak stawek kategorii - używana jest stawka domyślna dla wszystkich zajęć
                    </Typography>
                  )}
                </Box>
                
                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MoneyIcon />}
                    onClick={() => handleOpenRateDialog(trainer)}
                  >
                    Dodaj stawkę kategorii
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(trainer)}
                  >
                    Edytuj
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(trainer)}
                  >
                    Usuń
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
          
          {trainers.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Brak trenerów w systemie. Kliknij "Dodaj Trenera" aby dodać pierwszego trenera.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Dodaj nowego trenera
          </Box>
          <IconButton size="small" onClick={() => setCreateDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Imię i nazwisko"
              fullWidth
              required
              value={createForm.full_name}
              onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
              helperText="Pełne imię i nazwisko trenera"
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              helperText="Adres email trenera (musi być unikalny)"
            />
            <TextField
              label="Domyślna stawka godzinowa"
              type="number"
              fullWidth
              value={createForm.hourly_cost}
              onChange={(e) => setCreateForm({ ...createForm, hourly_cost: e.target.value })}
              InputProps={{
                endAdornment: <InputAdornment position="end">PLN/godz.</InputAdornment>,
                inputProps: { min: 0, step: 1 }
              }}
              helperText="Stawka używana gdy nie ma specyficznej stawki dla kategorii"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} startIcon={<CloseIcon />}>
            Anuluj
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSave}
            startIcon={<AddIcon />}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            Edytuj trenera
          </Box>
          <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Imię i nazwisko"
              fullWidth
              value={editForm.full_name}
              onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            />
            <TextField
              label="Domyślna stawka godzinowa"
              type="number"
              fullWidth
              value={editForm.hourly_cost}
              onChange={(e) => setEditForm({ ...editForm, hourly_cost: e.target.value })}
              InputProps={{
                endAdornment: <InputAdornment position="end">PLN/godz.</InputAdornment>,
                inputProps: { min: 0, step: 1 }
              }}
              helperText="Stawka używana gdy nie ma specyficznej stawki dla kategorii"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<CloseIcon />}>
            Anuluj
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSave}
            startIcon={<SaveIcon />}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Rate Dialog */}
      <Dialog 
        open={rateDialogOpen} 
        onClose={() => setRateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon color="primary" />
            Stawki dla kategorii - {rateDialogTrainer?.full_name}
          </Box>
          <IconButton size="small" onClick={() => setRateDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Existing rates */}
          {rateDialogTrainer?.category_rates?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Aktualne stawki kategorii:
              </Typography>
              <List dense disablePadding>
                {rateDialogTrainer.category_rates.map((rate) => (
                  <ListItem 
                    key={rate.id}
                    sx={{ bgcolor: 'grey.50', borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={rate.lesson_category_name}
                      secondary={formatCurrency(rate.hourly_rate)}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCategoryRate(rateDialogTrainer.id, rate.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {/* Add new rate */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Dodaj nową stawkę:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Kategoria zajęć</InputLabel>
              <Select
                value={newRateCategory}
                onChange={(e) => setNewRateCategory(e.target.value)}
                label="Kategoria zajęć"
              >
                {getAvailableCategoriesForTrainer(rateDialogTrainer || {}).map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Stawka"
              type="number"
              size="small"
              value={newRateAmount}
              onChange={(e) => setNewRateAmount(e.target.value)}
              InputProps={{
                endAdornment: <InputAdornment position="end">PLN</InputAdornment>,
                inputProps: { min: 0, step: 1 }
              }}
              sx={{ minWidth: 150 }}
            />
            <Button
              variant="contained"
              onClick={handleAddCategoryRate}
              disabled={!newRateCategory || !newRateAmount}
            >
              Dodaj
            </Button>
          </Box>
          
          {getAvailableCategoriesForTrainer(rateDialogTrainer || {}).length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Wszystkie kategorie zajęć mają już przypisane stawki.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRateDialogOpen(false)}>
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Potwierdź usunięcie
        </DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć trenera <strong>{trainerToDelete?.full_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ta operacja jest nieodwracalna. Trener nie może mieć przypisanych zajęć.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Anuluj
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
            startIcon={<DeleteIcon />}
          >
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrainerManagement;
